import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { InfoLinksConfig } from "@/types/agent/info-links";
import QRCode from "qrcode.react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface InfoPopoverProps {
  brandColor: string;
  title?: string;
  description?: string | null;
  variant?: "modal" | "popover";
  infoLinks?: InfoLinksConfig | null;
  qrLogoUrl?: string | null;
  subdued?: boolean;
  triggerPx?: number;
  /** Idle background override for the trigger button (replaces the default white). */
  idleBackgroundColor?: string | null;
  /** Icon color override for the trigger button (replaces brandColor). */
  iconColor?: string | null;
}

export const InfoPopover: React.FC<InfoPopoverProps> = ({
  brandColor,
  title = "About this Genie",
  description,
  variant = "modal",
  infoLinks,
  qrLogoUrl,
  subdued = false,
  triggerPx = 44,
  idleBackgroundColor,
  iconColor,
}) => {
  const [open, setOpen] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [pageUrl, setPageUrl] = useState("");
  const [isQrExpanded, setIsQrExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== "popover") return;

    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPageUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setIsQrExpanded(false);
    }
  }, [open]);

  // Links now live in the dedicated Resources drawer; the About panel keeps
  // just the description, an optional banner image, and the QR code.
  const showBanner = infoLinks?.banner_image_url && !bannerError;
  const hasExtras = !!showBanner;

  const descriptionContent = (
    <div style={{ fontSize: "0.95rem", color: "#374151", lineHeight: 1.5 }}>
      {description && description.trim().length > 0
        ? description
        : "This Genie can answer questions, guide you, and take actions based on its configuration."}
    </div>
  );

  const extrasContent = hasExtras ? (
    <div>
      {/* Divider */}
      <div style={{ borderTop: "1px solid #E5E7EB", margin: "12px 0" }} />

      {/* Banner image */}
      {showBanner && (
        <img
          src={infoLinks!.banner_image_url!}
          alt=""
          loading="lazy"
          onError={() => setBannerError(true)}
          style={{ width: "100%", borderRadius: 8, marginBottom: 0 }}
        />
      )}
    </div>
  ) : null;

  const qrContent = pageUrl ? (
    <div style={{ marginTop: 12 }}>
      <div className=" my-4" />
      <div className="text-sm text-slate-600 font-medium mb-2 text-center">
        Scan to open on your phone
      </div>
      <button
        type="button"
        onClick={() => {
          if (variant === "modal") {
            setIsQrExpanded((prev) => !prev);
          }
        }}
        style={{
          width: variant === "modal" && isQrExpanded ? "90%" : "auto",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: variant === "modal" ? "zoom-in" : "default",
          transition: "width 0.2s ease",
        }}
        aria-label={isQrExpanded ? "Collapse QR code" : "Expand QR code"}
        title={variant === "modal" ? "Tap to resize QR code" : undefined}
      >
        <QRCode
          value={pageUrl}
          size={variant === "modal" ? (isQrExpanded ? 340 : 180) : 120}
          level="H"
          includeMargin={true}
          imageSettings={
            qrLogoUrl
              ? {
                src: qrLogoUrl,
                height: variant === "modal" ? (isQrExpanded ? 52 : 30) : 20,
                width: variant === "modal" ? (isQrExpanded ? 52 : 30) : 20,
                excavate: true,
              }
              : undefined
          }
        />
      </button>
    </div>
  ) : null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: triggerPx,
          height: triggerPx,
          borderRadius: 9999,
          background: idleBackgroundColor || "white",
          border: subdued ? "1px solid #E5EAF0" : `1px solid ${brandColor}33`,
          color: iconColor || (subdued ? "#7C8B9C" : brandColor),
          boxShadow: subdued ? "0 1px 3px rgba(15,23,42,0.06)" : "0 4px 10px rgba(0,0,0,0.08)",
        }}
        aria-label="Genie info"
        title="Genie info"
      >
        <Info size={triggerPx <= 36 ? 16 : 20} />
      </button>

      {variant === "modal" ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="max-w-md"
            onPointerDownOutside={(event) => event.preventDefault()}
            onEscapeKeyDown={(event) => event.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg font-semibold text-slate-900 text-center">
                {title}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription asChild>
              <div className="mt-1 max-h-[70vh] overflow-y-auto text-sm leading-relaxed text-slate-600">
                {descriptionContent}
                {extrasContent}
                {qrContent}
              </div>
            </DialogDescription>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="default" className="w-full">
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        open && (
          <div
            style={{ position: "absolute", top: 44, left: 0, background: "#fff", border: "1px solid #E5E7EB", boxShadow: "0 12px 28px rgba(0,0,0,0.12)", borderRadius: 12, padding: 10, width: 260, zIndex: 50, maxHeight: "60vh", overflowY: "auto" }}
            role="dialog"
            aria-label="Genie info"
          >
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", marginBottom: 6 }}>{title}</div>
            {descriptionContent}
            {extrasContent}
            {qrContent}
          </div>
        )
      )}
    </div>
  );
};

export default InfoPopover;
